import React, { useEffect, useState, useRef } from "react";
import { useParams, useNavigate, useSearchParams } from "react-router-dom";
import { Button, Spinner, Form } from "react-bootstrap";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faArrowLeft,
  faSave,
  faFileArrowUp,
} from "@fortawesome/free-solid-svg-icons";
import axios from "axios";
import ViewerV2 from "../../components/ViewerV2/components/Viewer";
import AuthCheck from "../../components/AuthCheck";
import {
  compressLabelmapData,
  decompressAndLoadLabelmap,
} from "../../components/ViewerV2/utils/segmentationCompression";
import { getRenderingEngine, cache } from "@cornerstonejs/core";
import * as cornerstoneTools from "@cornerstonejs/tools";
import { computeContourData } from "@cornerstonejs/polymorphic-segmentation";

export default function DraftEditor() {
  const { studentnumber, caseId, draftId } = useParams();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const viewerRef = useRef(null);

  const [caseData, setCaseData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [draftName, setDraftName] = useState("");
  const [loadingSegmentation, setLoadingSegmentation] = useState(false);
  const [draftSegmentationData, setDraftSegmentationData] = useState(null);
  const [availableOrgans, setAvailableOrgans] = useState([]);

  useEffect(() => {
    if (!caseId) return;
    axios
      .get(`/api/editor/organs/${caseId}`, { withCredentials: false })
      .then((res) => setAvailableOrgans(res.data || []))
      .catch((err) => {
        console.error("Failed to load template organs:", err);
        setAvailableOrgans([]);
      });
  }, [caseId]);

  // Clear cache on component mount
  useEffect(() => {
    clearCornerstoneCache();
  }, []);

  // Load draft and case data
  useEffect(() => {
    loadData();

    // Setup auto-save every 30 seconds
    const interval = setInterval(() => {
      handleAutoSave();
    }, 30000);

    return () => {
      clearInterval(interval);
    };
    // eslint-disable-next-line
  }, [draftId, caseId]);

  // Wait for volume to be ready and load draft segmentation
  useEffect(() => {
    if (!draftSegmentationData) return;

    const checkAndLoadDraft = () => {
      try {
        const volume = cache.getVolume("DRAFT_SEGMENTATION");
        if (volume) {
          console.log("Volume is ready, loading draft...");
          const success = decompressAndLoadLabelmap(
            "DRAFT_SEGMENTATION",
            draftSegmentationData,
          );

          if (success) {
            // Trigger rendering update
            const renderingEngine = getRenderingEngine("myRenderingEngine");
            if (renderingEngine) {
              const viewportIds = ["CT_AXIAL", "CT_SAGITTAL", "CT_CORONAL"];
              viewportIds.forEach((viewportId) => {
                const viewport = renderingEngine.getViewport(viewportId);
                if (viewport) {
                  viewport.render();
                }
              });
            }
            console.log("Draft loaded successfully");
            setDraftSegmentationData(null); // Clear to prevent reloading
          } else {
            console.error("Failed to load draft segmentation");
          }

          // Wait a bit for colors to be configured, then clear loading
          setTimeout(() => {
            setLoadingSegmentation(false);
          }, 500);
        } else {
          // Volume not ready yet, try again
          setTimeout(checkAndLoadDraft, 200);
        }
      } catch (error) {
        console.error("Error checking for volume:", error);
        setTimeout(checkAndLoadDraft, 200);
      }
    };

    // Start checking after a brief delay
    setTimeout(checkAndLoadDraft, 500);
  }, [draftSegmentationData]);

  function clearCornerstoneCache() {
    try {
      console.log("Clearing Cornerstone cache...");

      // Clear segmentation state
      try {
        cornerstoneTools.segmentation.state.removeAllSegmentations();
        console.log("Cleared all segmentations");
      } catch (error) {
        console.warn("Error clearing segmentation state:", error);
      }

      // Remove specific segmentation volumes from cache
      try {
        const labelmapVolume = cache.getVolume("DRAFT_SEGMENTATION");
        if (labelmapVolume) {
          const sliceCount = labelmapVolume.dimensions?.[2] ?? 0;
          for (let i = 0; i < sliceCount; i++) {
            const sid = `DRAFT_SEGMENTATION_slice_${i}`;
            if (cache.getImageLoadObject(sid)) {
              try {
                cache.removeImageLoadObject(sid);
              } catch (e) {
                /* ignore */
              }
            }
          }
          cache.removeVolumeLoadObject("DRAFT_SEGMENTATION");
          console.log("Removed DRAFT_SEGMENTATION from cache");
        }
      } catch (error) {
        console.warn("Error removing labelmap volume:", error);
      }

      try {
        const contourVolume = cache.getVolume("DRAFT_CONTOUR_SEGMENTATION");
        if (contourVolume) {
          cache.removeVolumeLoadObject("DRAFT_CONTOUR_SEGMENTATION");
          console.log("Removed DRAFT_CONTOUR_SEGMENTATION from cache");
        }
      } catch (error) {
        console.warn("Error removing contour volume:", error);
      }

      console.log("Cache cleared successfully");
    } catch (error) {
      console.error("Error clearing Cornerstone cache:", error);
    }
  }

  function loadData() {
    setLoading(true);

    // Load case data
    axios
      .get(`/api/student/${studentnumber}/case/${caseId}`, {
        withCredentials: false,
      })
      .then((response) => {
        setCaseData(response.data);

        // If editing existing draft, load it
        if (draftId && draftId !== "new") {
          return axios.get(
            `/api/student/${studentnumber}/case/${caseId}/draft/${draftId}`,
            {
              withCredentials: false,
            },
          );
        } else {
          // New draft - get name from query params if available
          const nameFromQuery = searchParams.get("name");
          setDraftName(
            nameFromQuery || `Draft ${new Date().toLocaleDateString()}`,
          );
          return Promise.resolve(null);
        }
      })
      .then((draftResponse) => {
        if (draftResponse && draftResponse.data) {
          const draftData = draftResponse.data;
          setDraftName(
            draftData.organName || draftData.name || "Unnamed Draft",
          );

          // Load the segmentation data if it exists
          if (draftData.segmentationData) {
            // Parse JSON string if needed
            const segData =
              typeof draftData.segmentationData === "string"
                ? JSON.parse(draftData.segmentationData)
                : draftData.segmentationData;

            // Store for later loading after viewer is ready
            setDraftSegmentationData(segData);
            setLoadingSegmentation(true);
          }
        }
        setLoading(false);
      })
      .catch((err) => {
        console.error("Error loading data:", err);
        setLoading(false);
      });
  }

  function handleAutoSave() {
    // Auto-save disabled for now - only manual save to avoid overwhelming the server
    // Uncomment below to enable auto-save
    /*
    if (!draftName.trim() || draftId === "new") return;

    const compressedData = compressLabelmapData('DRAFT_SEGMENTATION');
    if (!compressedData) return;

    const saveData = {
      organName: draftName,
      segmentationData: JSON.stringify(compressedData)
    };

    axios.put(`/api/student/${studentnumber}/case/${caseId}/draft/${draftId}`, saveData, {
      withCredentials: false,
    })
      .then(() => {
        console.log("Auto-saved draft");
      })
      .catch((err) => {
        console.error("Error auto-saving draft:", err);
      });
    */
  }

  function handleSave() {
    if (!draftName.trim()) {
      alert("Please enter an organ name");
      return;
    }

    setSaving(true);

    // Compress the labelmap data
    const compressedData = compressLabelmapData("DRAFT_SEGMENTATION");

    if (!compressedData) {
      alert(
        "Failed to compress segmentation data. Check the browser console for details.",
      );
      setSaving(false);
      return;
    }

    const saveData = {
      organName: draftName,
      segmentationData: JSON.stringify(compressedData),
    };

    const url =
      draftId && draftId !== "new"
        ? `/api/student/${studentnumber}/case/${caseId}/draft/${draftId}`
        : `/api/student/${studentnumber}/case/${caseId}/draft`;

    const method = draftId && draftId !== "new" ? "put" : "post";

    axios[method](url, saveData, {
      withCredentials: false,
    })
      .then((response) => {
        setSaving(false);
        alert("Draft saved successfully!");

        // If this was a new draft, update URL with the new ID
        if (method === "post" && response.data.id) {
          navigate(
            `/student/${studentnumber}/case/${caseId}/draft/${response.data.id}`,
            { replace: true },
          );
        }
      })
      .catch((err) => {
        console.error("Error saving draft:", err);
        setSaving(false);
        alert("Failed to save draft. Please try again.");
      });
  }

  async function handleSubmit() {
    if (!draftName.trim()) {
      alert("Please enter a name for this draft");
      return;
    }
    if (!availableOrgans.length) {
      alert(
        "Template organs not loaded yet — wait a moment and try again, or refresh.",
      );
      return;
    }
    if (
      !window.confirm(
        "Are you sure you want to submit this draft? This action cannot be undone.",
      )
    ) {
      return;
    }

    // Pick the latest session for this case that has no submission yet.
    // Mirrors the timeline logic in CaseDetails.js.
    const submittedSessionIds = new Set(
      (caseData?.submissions || []).map((s) => s.session?.id).filter(Boolean),
    );
    const openSessions = (caseData?.sessions || []).filter(
      (s) => !submittedSessionIds.has(s.id),
    );
    const activeSession = openSessions.sort(
      (a, b) => new Date(b.createdOn).getTime() - new Date(a.createdOn).getTime(),
    )[0];

    if (!activeSession) {
      alert("No open session available to submit to.");
      return;
    }

    setSaving(true);

    try {
      const renderingEngine = getRenderingEngine("myRenderingEngine");
      const viewport = renderingEngine?.getViewport("CT_AXIAL");
      if (!viewport) throw new Error("Viewport not available");

      const segmentationId = "DRAFT_SEGMENTATION";
      const volume = cache.getVolume(segmentationId);
      if (!volume) throw new Error("No segmentation volume available");

      const scalarData = volume.voxelManager.getCompleteScalarDataArray();
      const segmentIndices = new Set();
      for (let i = 0; i < scalarData.length; i++) {
        if (scalarData[i] > 0) segmentIndices.add(scalarData[i]);
      }
      if (segmentIndices.size === 0) {
        setSaving(false);
        alert("Draft has no segmentation data to submit.");
        return;
      }

      const contourResult = await computeContourData(segmentationId, {
        segmentIndices: Array.from(segmentIndices),
        viewport: viewport,
      });
      if (!contourResult?.annotationUIDsMap) {
        throw new Error("Failed to extract contours from draft.");
      }

      const segmentContours = {};
      contourResult.annotationUIDsMap.forEach((uids, segmentIndex) => {
        const annotations = Array.from(uids)
          .map((uid) => cornerstoneTools.annotation.state.getAnnotation(uid))
          .filter(Boolean);
        segmentContours[segmentIndex] = annotations.map((annotation) => ({
          imageId: annotation.metadata?.referencedImageId || "",
          points: annotation.data?.contour?.polyline || [],
        }));
      });

      // Map each drawn segment to its template organ. The grading pipeline
      // matches RTSTRUCT ROIs against the gold-standard organ names, so any
      // segment whose index isn't in the template would be unscored — flag it
      // before sending instead of silently dropping or mislabeling.
      const unmappedSegments = [];
      const exportData = {
        segments: Object.entries(segmentContours).map(
          ([segmentIndex, contours]) => {
            const idx = parseInt(segmentIndex);
            const organ = availableOrgans.find((o) => o.segmentIndex === idx);
            if (!organ) unmappedSegments.push(idx);
            return {
              segmentIndex: idx,
              organName: organ?.name || `Segment ${idx}`,
              color: organ?.color || [255, 0, 0],
              contours: contours,
            };
          },
        ),
      };

      if (unmappedSegments.length) {
        const proceed = window.confirm(
          `Segment ${unmappedSegments.join(", ")} ${
            unmappedSegments.length === 1 ? "isn't" : "aren't"
          } in the case template and won't be graded. Submit anyway?`,
        );
        if (!proceed) {
          setSaving(false);
          return;
        }
      }

      const dicomResponse = await axios.post(
        `/api/student/${studentnumber}/case/${caseId}/export-rtstruct`,
        exportData,
        { responseType: "blob", withCredentials: false },
      );

      const formData = new FormData();
      formData.append(
        "file",
        dicomResponse.data,
        `draft-${draftId || "new"}.dcm`,
      );

      const uploadResponse = await axios.post(
        `/api/student/upload/struct/${caseId}/${studentnumber}/${activeSession.id}`,
        formData,
        { withCredentials: false },
      );

      setSaving(false);
      const ok = String(uploadResponse.data || "").startsWith("true");
      if (ok) {
        alert("Draft submitted successfully!");
        navigate(`/student/${studentnumber}`);
      } else {
        alert(
          "Submit failed: server rejected the DICOM. Check that the organ name matches the case template.",
        );
      }
    } catch (err) {
      console.error("Error submitting draft:", err);
      setSaving(false);
      alert(
        "Failed to submit draft: " +
          (err?.response?.data?.message || err?.message || "unknown error"),
      );
    }
  }

  function handleBack() {
    if (
      window.confirm(
        "Are you sure you want to go back? Any unsaved changes will be lost.",
      )
    ) {
      navigate(`/student/${studentnumber}`);
    }
  }

  if (loading) {
    return (
      <div
        className="d-flex justify-content-center align-items-center"
        style={{ height: "100vh" }}
      >
        <Spinner animation="border" />
      </div>
    );
  }

  return (
    <>
      <AuthCheck />
      <div
        style={{
          display: "flex",
          flexDirection: "column",
          height: "calc(100vh - 56px)",
        }}
      >
        {/* Toolbar */}
        <div
          style={{
            backgroundColor: "var(--sidebar-bg)",
            borderBottom: "1px solid var(--modal-header-border)",
            padding: "10px 20px",
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
          }}
        >
          <div style={{ display: "flex", alignItems: "center", gap: "15px" }}>
            <Button variant="outline-secondary" size="sm" onClick={handleBack}>
              <FontAwesomeIcon icon={faArrowLeft} /> Back
            </Button>

            <div
              style={{
                borderLeft: "1px solid var(--modal-header-border)",
                height: "30px",
              }}
            ></div>

            <Form.Control
              type="text"
              value={draftName}
              onChange={(e) => setDraftName(e.target.value)}
              style={{ width: "250px" }}
              placeholder="Organ name (e.g., Liver)"
              autoComplete="off"
              data-lpignore="true"
              data-form-type="other"
            />

            {caseData && (
              <small className="text-muted">
                Case: <b>{caseData.name}</b>
              </small>
            )}
          </div>

          <div style={{ display: "flex", alignItems: "center", gap: "10px" }}>
            <Button
              variant="primary"
              size="sm"
              onClick={handleSave}
              disabled={saving}
            >
              <FontAwesomeIcon icon={faSave} /> {saving ? "Saving..." : "Save"}
            </Button>

            <Button
              variant="success"
              size="sm"
              onClick={handleSubmit}
              disabled={saving}
            >
              <FontAwesomeIcon icon={faFileArrowUp} /> Submit
            </Button>
          </div>
        </div>

        {/* Viewer with Editor Mode enabled */}
        <div style={{ flex: 1, overflow: "hidden" }}>
          {caseData && (
            <ViewerV2
              ref={viewerRef}
              case={caseData.id}
              editorMode={true}
              loadingSegmentation={loadingSegmentation}
              onToolChange={(tool) => console.log("Tool changed:", tool)}
            />
          )}
        </div>
      </div>
    </>
  );
}
